import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import AddChartModal from "./AddChartModal";
import useAxiosInstance from "../../../../../lib/useAxiosInstance";
import BarChartSection from "./BarChartSection";
import ProgressChartSection from "./ProgressChartSection";
import PieChartSection from "./PieChartSection";
import { Button, Tooltip } from "antd";
import { ChevronDown, ChevronUp } from "lucide-react";
import Summary from "./Summary";
import DeleteChartModal from "./DeleteChartModal"; 
import LegendSection from "./LegendSection";

const AGE_PIE_CONFIG_ID = "age-distribution";

const buildFilterParams = (filters) => ({
  constituency: filters?.constituency || undefined,
  village: filters?.village || undefined,
  booth: filters?.booth || undefined,
});

const TabOne = ({ survey, filters }) => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();
  const surveyId = survey?._id || survey?.id;

  const [openModal, setOpenModal] = useState(false);
  const [activeChartType, setActiveChartType] = useState(null);

  const [showSummary, setShowSummary] = useState(false);

  const [selectedSummaryId, setSelectedSummaryId] = useState("");
  const [selectedBarConfigId, setSelectedBarConfigId] = useState("");
  const [selectedProgressConfigId, setSelectedProgressConfigId] = useState("");
  const [selectedPieConfigId, setSelectedPieConfigId] = useState(AGE_PIE_CONFIG_ID);

  // NEW: Selected Legend Config ID (for fetching)
  const [selectedLegendConfigId, setSelectedLegendConfigId] = useState("");

  // DELETE MODAL STATES
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: allConfigs = [] } = useQuery({
    refetchOnWindowFocus: false,
    retry: 1,
    queryKey: ["chartConfigs", surveyId],
    enabled: !!surveyId,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/api/surveyAnalytics/allChartConfig/${surveyId}`
      );
      const configs = res?.data?.data || [];

      if (!selectedSummaryId) {
        const firstSummary = configs.find(
          (cfg) => cfg.chartType === "summary" || cfg.chart_type === "summary"
        );
        if (firstSummary) setSelectedSummaryId(firstSummary._id);
      }

      if (!selectedBarConfigId) {
        const firstBar = configs.find(
          (cfg) => cfg.chartType === "bar" || cfg.chart_type === "bar"
        );
        if (firstBar) setSelectedBarConfigId(firstBar._id);
      }

      if (!selectedProgressConfigId) {
        const firstProgress = configs.find(
          (cfg) => cfg.chartType === "progress" || cfg.chart_type === "progress"
        );
        if (firstProgress) setSelectedProgressConfigId(firstProgress._id);
      }

      if (!selectedPieConfigId) {
        const firstPie = configs.find(
          (cfg) => cfg.chartType === "pie" || cfg.chart_type === "pie"
        );
        setSelectedPieConfigId(firstPie ? firstPie._id : AGE_PIE_CONFIG_ID);
      }

      // NEW: Auto-select legend config
      if (!selectedLegendConfigId) {
        const firstLegend = configs.find(
          (cfg) => cfg.chartType === "legend" || cfg.chart_type === "legend"
        );
        if (firstLegend) setSelectedLegendConfigId(firstLegend._id);
      }

      return configs;
    },
  });

  // CONFIG GROUPING
  const barConfigs = allConfigs.filter(
    (cfg) => cfg.chartType === "bar" || cfg.chart_type === "bar"
  );
  const progressConfigs = allConfigs.filter(
    (cfg) => cfg.chartType === "progress" || cfg.chart_type === "progress"
  );
  const pieConfigsFromApi = allConfigs.filter(
    (cfg) => cfg.chartType === "pie" || cfg.chart_type === "pie"
  );

  const hasSummaryConfig = allConfigs.some(
    (cfg) => cfg.chartType === "summary" || cfg.chart_type === "summary"
  );

  const hasLegendConfig = allConfigs.some(
    (cfg) => cfg.chartType === "legend" || cfg.chart_type === "legend"
  );

  const pieConfigs = [
    { _id: AGE_PIE_CONFIG_ID, heading: "Age Distribution", chartType: "pie" },
    ...pieConfigsFromApi,
  ];

  const filterParams = buildFilterParams(filters);

  // FETCH SUMMARY
  const { data: summaryData = [] } = useQuery({
    refetchOnWindowFocus: false,
    retry: 1,
    queryKey: ["chartConfigData", "summary", selectedSummaryId, filterParams],
    enabled: !!selectedSummaryId,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/api/surveyAnalytics/chartConfig/${surveyId}/summary`,
        { params: filterParams }
      );
      return res?.data?.data || [];
    },
  });

  // FETCH BAR
  const { data: barChartData = [] } = useQuery({
    refetchOnWindowFocus: false,
    retry: 1,
    queryKey: ["chartConfigData", "bar", selectedBarConfigId, filterParams],
    enabled: !!selectedBarConfigId,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/api/surveyAnalytics/chartConfig/${selectedBarConfigId}`,
        { params: filterParams }
      );
      return res?.data?.data?.data || [];
    },
  });

  // FETCH PROGRESS
  const { data: progressChartData = [] } = useQuery({
    refetchOnWindowFocus: false,
    retry: 1,
    queryKey: ["chartConfigData", "progress", selectedProgressConfigId, filterParams],
    enabled: !!selectedProgressConfigId,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/api/surveyAnalytics/chartConfig/${selectedProgressConfigId}`,
        { params: filterParams }
      );
      return res?.data?.data?.data || [];
    },
  });

  // FETCH PIE
  const { data: pieChartData = [] } = useQuery({
    refetchOnWindowFocus: false,
    retry: 1,
    queryKey: ["chartConfigData", "pie", selectedPieConfigId, filterParams],
    enabled: !!selectedPieConfigId && selectedPieConfigId !== AGE_PIE_CONFIG_ID,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/api/surveyAnalytics/chartConfig/${selectedPieConfigId}`,
        { params: filterParams }
      );
      return res?.data?.data?.data || [];
    },
  });

  // FETCH AGE PIE
  const { data: agePieChartData = [] } = useQuery({
    refetchOnWindowFocus: false,
    retry: 1,
    queryKey: ["ageDistribution", surveyId, filterParams],
    enabled: !!surveyId,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/api/surveyAnalytics/chartConfig/${surveyId}/ageDistribution`,
        { params: filterParams }
      );
      return res?.data?.data?.data || [];
    },
  });

  const effectivePieChartData =
    selectedPieConfigId === AGE_PIE_CONFIG_ID ? agePieChartData : pieChartData;

  // NEW: FETCH LEGEND DATA
  const { data: legendData = [] } = useQuery({
    refetchOnWindowFocus: false,
    retry: 1,
    queryKey: ["chartConfigData", "legend", selectedLegendConfigId, filterParams],
    enabled: !!selectedLegendConfigId && hasLegendConfig, 
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/api/surveyAnalytics/chartConfig/${selectedLegendConfigId}/colorLegend?surveyType=${survey?.surveyType}`,
        { params: filterParams }
      );
      return res?.data?.data || [];
    },
  });

  // ADD + DELETE HANDLERS (unchanged)
  const handleAddClick = (chartType) => {
    setActiveChartType(chartType);
    setOpenModal(true);
  };

  const handleConfigSaved = (savedConfig) => {
    queryClient.invalidateQueries(["chartConfigs", surveyId]);

    const type = savedConfig.chartType || savedConfig.chart_type;

    if (type === "bar") setSelectedBarConfigId(savedConfig._id);
    else if (type === "progress") setSelectedProgressConfigId(savedConfig._id);
    else if (type === "pie") setSelectedPieConfigId(savedConfig._id);
    else if (type === "summary") {
      setSelectedSummaryId(savedConfig._id);
      setShowSummary(true);
    } else if (type === "legend") {
      setSelectedLegendConfigId(savedConfig._id);
    }

    setOpenModal(false);
  };

  const handleLegendColorSave = async (textValue, color) => {
    try {
      await axiosInstance.post(
        `/api/surveyAnalytics/chartConfig/${selectedLegendConfigId}/saveColor`,
        {
          text_value: textValue,
          color,
          surveyId
        }
      );

      queryClient.invalidateQueries([
        "chartConfigData",
        "legend",
        selectedLegendConfigId,
      ]);
    } catch (err) {
      console.error("Error saving legend color:", err);
    }
  };

  const openDeleteModalForType = (chartType) => {
    let id = null;

    if (chartType === "bar") id = selectedBarConfigId;
    else if (chartType === "progress") id = selectedProgressConfigId;
    else if (chartType === "pie") {
      if (selectedPieConfigId === AGE_PIE_CONFIG_ID) return;
      id = selectedPieConfigId;
    } else if (chartType === "summary") id = selectedSummaryId;
    else if (chartType === "legend") id = selectedLegendConfigId;

    if (!id) return;

    const cfg = allConfigs.find((c) => c._id === id);

    setConfigToDelete({ id, type: chartType, heading: cfg?.heading });
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!configToDelete?.id) return;

    const { id: deletedId, type } = configToDelete;

    try {
      setIsDeleting(true);
      await axiosInstance.delete(`/api/surveyAnalytics/chartConfig/${deletedId}`);

      if (type === "bar" && selectedBarConfigId === deletedId) {
        const next = barConfigs.find((cfg) => cfg._id !== deletedId);
        setSelectedBarConfigId(next?._id || "");
      }

      if (type === "progress" && selectedProgressConfigId === deletedId) {
        const next = progressConfigs.find((cfg) => cfg._id !== deletedId);
        setSelectedProgressConfigId(next?._id || "");
      }

      if (type === "pie" && selectedPieConfigId === deletedId) {
        const next = pieConfigsFromApi.find((cfg) => cfg._id !== deletedId);
        setSelectedPieConfigId(next?._id || AGE_PIE_CONFIG_ID);
      }

      if (type === "summary" && selectedSummaryId === deletedId) {
        setSelectedSummaryId("");
        setShowSummary(false);
      }

      if (type === "legend" && selectedLegendConfigId === deletedId) {
        setSelectedLegendConfigId("");
      }

      queryClient.invalidateQueries(["chartConfigs", surveyId]);
      queryClient.invalidateQueries("chartConfigData");
    } catch (error) {
      console.error("Error deleting chart config:", error);
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setConfigToDelete(null);
    }
  };

  const handleCloseDeleteModal = () => {
    if (isDeleting) return;
    setDeleteModalOpen(false);
    setConfigToDelete(null);
  };
  return (
    <>
      {/* Top Right Charts */}
      <div className="absolute top-20 right-[1%] z-[9999] w-[35rem]">
        <div className="rounded-[28px] bg-white/70 backdrop-blur-xl border border-white/80 shadow-[0_18px_45px_rgba(15,23,42,0.25)] flex flex-col gap-4 p-4">
          <div className="flex gap-3 justify-center items-center">
            <BarChartSection
              configs={barConfigs}
              selectedConfigId={selectedBarConfigId}
              setSelectedConfigId={setSelectedBarConfigId}
              chartData={barChartData}
              onAdd={() => handleAddClick("bar")}
              onDelete={() => openDeleteModalForType("bar")}
            />

            <ProgressChartSection
              configs={progressConfigs}
              selectedConfigId={selectedProgressConfigId}
              setSelectedConfigId={setSelectedProgressConfigId}
              chartData={progressChartData}
              onAdd={() => handleAddClick("progress")}
              onDelete={() => openDeleteModalForType("progress")}
            />
          </div>

          <PieChartSection
            configs={pieConfigs}
            selectedConfigId={selectedPieConfigId}
            setSelectedConfigId={setSelectedPieConfigId}
            chartData={effectivePieChartData}
            onAdd={() => handleAddClick("pie")}
            onDelete={() => openDeleteModalForType("pie")}
          />
        </div>

        {openModal && (
          <AddChartModal
            survey={survey}
            chartType={activeChartType}
            onClose={() => setOpenModal(false)}
            onSave={handleConfigSaved}
          />
        )}
      </div>

      {/* SUMMARY BUTTON */}
      {hasSummaryConfig ? (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-[30] flex text-center">
          <Tooltip title={showSummary ? "Hide Feedback" : "View Feedback Summary"}>
            <Button
              type="primary"
              shape="circle"
              size="large"
              icon={showSummary ? <ChevronDown size={22} /> : <ChevronUp size={22} />}
              className="shadow-xl bg-[#0b65c2] hover:bg-[#123865] transition-transform hover:scale-110 flex justify-center"
              onClick={() => setShowSummary(!showSummary)}
            />
          </Tooltip>
        </div>
      ) : (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-[30] flex text-center">
          <Button
            type="primary"
            size="large"
            className="shadow-xl bg-[#0b65c2] hover:bg-[#123865] transition-transform hover:scale-110 flex justify-center rounded-full px-6"
            onClick={() => handleAddClick("summary")}
          >
            Add Summary
          </Button>
        </div>
      )}

      {/* SUMMARY VIEW */}
      {showSummary && hasSummaryConfig && (
        <Summary
          data={summaryData}
          onClose={() => setShowSummary(false)}
          onAdd={() => handleAddClick("summary")}
          surveyId={surveyId}
          onDelete={() => openDeleteModalForType("summary")}
        />
      )}

      {/* ADD LEGEND BUTTON */}
      {!hasLegendConfig && (
        <div className="absolute bottom-4 left-4 z-[9999]">
          <Button
            type="primary"
            shape="circle"
            size="large"
            className="bg-[#0b65c2] hover:bg-[#123865]"
            onClick={() => handleAddClick("legend")}
          >
            +
          </Button>
        </div>
      )}

      {hasLegendConfig && (
        <div className="absolute bottom-[1%] left-4 z-[999] w-72">
          <LegendSection
            configs={allConfigs.filter(
              (cfg) => cfg.chartType === "legend" || cfg.chart_type === "legend"
            )}
            surveyId={surveyId}
            surveyType={survey?.surveyType}
            selectedConfigId={selectedLegendConfigId}
            setSelectedConfigId={setSelectedLegendConfigId}
            legendData={legendData}
            onDelete={() => openDeleteModalForType("legend")}
            onAddColor={(textValue, color) => handleLegendColorSave(textValue, color)}
          />
        </div>
      )}

      {/* DELETE MODAL */}
      {deleteModalOpen && configToDelete && (
        <DeleteChartModal
          heading={configToDelete.heading}
          chartType={configToDelete.type}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          isSubmitting={isDeleting}
        />
      )}
    </>
  );
};

export default TabOne;
